type ElementGroup = {
  elements: (string | number)[];
  top: number;
  bottom: number;
};

const ELEMENT_HEIGHT = 15; // px

/**
 * Move SVG elements up and down to remove any overlap they have.
 * @param startYPositions
 */
export function getVerticallySpacedLabelYs(
  startYPositions: Map<string | number, number>,
  min: number,
  max: number,
  lineHeight = ELEMENT_HEIGHT
) {
  let elementsWereMoved = false;

  const yPositions = new Map<string | number, number>(startYPositions);

  const keysSorted = Array.from(startYPositions.keys()).sort(
    (a, b) => (startYPositions.get(a) ?? 0) - (startYPositions.get(b) ?? 0)
  );

  // Order the elements in groups
  let lastGroup: ElementGroup | undefined;
  const groups = [];
  for (const key of keysSorted) {
    if (!lastGroup) {
      lastGroup = {
        elements: [],
        top: 0,
        bottom: 0
      };
      groups.push(lastGroup);
    }

    const y = startYPositions.get(key) ?? 0;

    const lastEntry = lastGroup?.elements[lastGroup.elements.length - 1];
    if (!lastEntry) {
      lastGroup.elements.push(key);
      lastGroup.top = y;
      lastGroup.bottom = y + lineHeight;
      continue;
    }

    const lastY = startYPositions.get(lastEntry) ?? 0;

    if (lastY + lineHeight >= y) {
      lastGroup.elements.push(key);
      lastGroup.bottom = y + lineHeight;
      continue;
    }

    lastGroup = {
      elements: [key],
      top: y,
      bottom: y + lineHeight
    };
    groups.push(lastGroup);
  }

  // spread the elements in the groups
  for (const group of groups) {
    const elements = group.elements;

    const yStart = group.top;
    const yEnd = group.bottom;

    const heightBefore = yEnd - yStart;
    const heightAfter = elements.length * ELEMENT_HEIGHT;
    const heightDifference = heightAfter - heightBefore;
    const yOffset = -heightDifference / 2;

    group.top = yStart + yOffset;
    group.bottom = group.top + heightAfter;

    if (elements.length > 1) {
      moveGroup(group, yPositions, 0, lineHeight);
      elementsWereMoved = true;
    }
  }

  // spread the groups
  spreadGroups(groups, yPositions, lineHeight);

  if (groups.length > 0) {
    // make sure the min bound is respected.
    if (groups[0].top < min) {
      dominoMove(groups, yPositions, min - groups[0].top, lineHeight);
    }
    if (groups[groups.length - 1].bottom > max) {
      dominoMoveBack(
        groups,
        yPositions,
        max - groups[groups.length - 1].bottom,
        lineHeight
      );
    }
  }

  return {
    moved: elementsWereMoved,
    positions: yPositions
  };

  // if (elementsWereMoved) {
  //   moveAndConnectLabels(labelNodes, startYPositions);
  // } else {
  //   // remove all connect lines in this SVG
  //   labelNodes[0].ownerSVGElement
  //     ?.querySelectorAll?.(".chart-label-connect-line")
  //     .forEach((node) => node.remove());
  // }
  // return elementsWereMoved;
}

function spreadGroups(
  groups: ElementGroup[],
  positions: Map<string | number, number>,
  lineHeight = ELEMENT_HEIGHT
) {
  let lastGroup: undefined | ElementGroup = undefined;

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];

    if (lastGroup) {
      if (lastGroup.bottom > group.top) {
        const difference = lastGroup.bottom - group.top;
        moveGroup(group, positions, difference / 2, lineHeight);
        dominoMoveBack(groups.slice(0, i), positions, -difference / 2, lineHeight);
      }
    }

    lastGroup = group;
  }
}

function moveGroup(
  group: ElementGroup,
  positions: Map<string | number, number>,
  amount = 0,
  lineHeight = ELEMENT_HEIGHT
) {
  group.top += amount;
  group.bottom += amount;
  for (let i = 0; i < group.elements.length; i++) {
    const labelKey = group.elements[i];
    positions.set(labelKey, group.top + lineHeight * i);
  }
}

function dominoMoveBack(
  groups: ElementGroup[],
  positions: Map<string | number, number>,
  amount = 0,
  lineHeight = ELEMENT_HEIGHT
) {
  if (groups.length == 0) return;

  let lastGroup = groups[groups.length - 1];
  moveGroup(lastGroup, positions, amount, lineHeight);

  for (let i = groups.length - 2; i >= 0; i--) {
    const group = groups[i];
    if (group.bottom > lastGroup.top) {
      const difference = lastGroup.top - group.bottom;
      moveGroup(group, positions, difference, lineHeight);
      lastGroup = group;
    } else {
      break;
    }
  }
}
function dominoMove(
  groups: ElementGroup[],
  positions: Map<string | number, number>,
  amount = 0,
  lineHeight = ELEMENT_HEIGHT
) {
  if (groups.length == 0) return;

  let lastGroup = groups[0];
  moveGroup(lastGroup, positions, amount, lineHeight);

  for (let i = 1; i < groups.length; i++) {
    const group = groups[i];
    if (group.top < lastGroup.bottom) {
      const difference = lastGroup.bottom - group.top;
      moveGroup(group, positions, difference, lineHeight);
      lastGroup = group;
    } else {
      break;
    }
  }
}

// /**
//  * Move all labels to the right and generate connecting lines to
//  * better show which line each label connects to.
//  * @param labels SVG Elements of all the labels.
//  * @param originalPositions Map of the Y positions of the line ends.
//  */
// function moveAndConnectLabels(
//   labels: SVGElement[],
//   originalPositions: Map<SVGElement, number>
// ) {
//   const LINE_X_MOVEMENT = 25; // in px
//   // remove all other connect lines in this SVG
//   const svg = labels[0].ownerSVGElement;
//   svg
//     ?.querySelectorAll?.(".chart-label-connect-line")
//     .forEach((node) => node.remove());

//   const W = LINE_X_MOVEMENT;
//   for (const label of labels) {
//     const x = parseInt(label.getAttribute("x") ?? "0");
//     label.setAttribute("x", x + W + "");

//     const originalY = originalPositions.get(label) ?? 0;
//     const y = parseInt(label.getAttribute("y") ?? "0");

//     const connectLine = document.createElementNS(
//       "http://www.w3.org/2000/svg",
//       "line"
//     );
//     connectLine.setAttribute("x1", x - 8 + "");
//     connectLine.setAttribute("x2", x + W - 3 + "");
//     connectLine.setAttribute("y1", originalY - 3 + "");
//     connectLine.setAttribute("y2", y - 3 + "");
//     connectLine.setAttribute("class", "chart-label-connect-line");

//     label.parentElement?.appendChild?.(connectLine);
//   }
// }
